/*
 * <pdp-aplicacao>: seção "Veja na pele" da PDP v2 (aplicação do produto por tom de pele).
 *
 * Contrato de markup gerado por snippets/biobio-pdp-aplicacao.liquid: régua
 * .pdp-ad-regua com células [data-apl-tom="<id numérico da variante>"] e um
 * <figure data-apl-item="<mesmo id>"> por tom, todos no DOM, só o ativo sem hidden.
 *
 * Régua portada de assets/biobio-pdp-antes-depois.js (marcarRegua, aoClicarRegua,
 * aoTeclarRegua): um só tabindex=0 no grupo, setas e Home/End movem o foco e já
 * selecionam (padrão WAI-ARIA de radiogroup).
 *
 * Reprodução: quem cuida dos video[data-trio-lazy-video] é o assets/biobio-trio.js
 * (lazy por IntersectionObserver, autoplay mudo, userPaused, pausa fora da tela,
 * aria-pressed do botão pelos eventos play e pause). Ele não expõe função nem
 * evento para carregar um vídeo, então aqui vai o mínimo: poster sob demanda
 * (mesma escolha de largura do Trio) e src = data-src (o MP4) antes do play. O
 * Trio, quando o observer dele alcança o vídeo, vê o src já posto e não troca.
 * O vídeo que sai é pausado SEM userPaused, para o observer do Trio voltar a
 * tocá-lo quando o tom voltar à tela. Com prefers-reduced-motion nada toca: o
 * poster aparece e o play fica com a cliente (botão do Trio).
 *
 * Troca de tom não mexe em oferta, carrinho, URL nem galeria. Sem estado global.
 * Emite 'aplicacao:tom-trocado' (detail.variantId numérico, bubbles) e
 * dataLayer.push({ event: 'pdp_v2_aplicacao_tom', tom: '<nome do tom>' }): no clique na hora; no teclado só
 * depois de 700 ms parado no mesmo tom (16/10, M3: cada seta contava um evento). O nome vem de
 * data-apl-nome (M2), com o aria-label de reserva para markup antigo em cache.
 *
 * Guard de registro no topo: o Shopify NÃO dedupa <script> por URL.
 */
(function () {
  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('pdp-aplicacao')) return;

  var LAYOUT_POSTER_CELULAR = '(max-width: 749px)';

  var ESPERA_TECLADO = 700;

  // "Ver a aplicação numa pele como esta (tom Amêndoa)" -> "Amêndoa". Reserva para markup sem data-apl-nome.
  function nomeDoTom(rotulo) {
    var m = /\(tom\s+([^)]*)\)\s*$/.exec(rotulo || '');
    return m ? m[1].trim() : '';
  }

  function midia(q) {
    return !!(window.matchMedia && window.matchMedia(q).matches);
  }

  class PdpAplicacao extends HTMLElement {
    connectedCallback() {
      if (this._pronto) return;
      this.regua = this.querySelector('.pdp-ad-regua');
      this.celulas = this.regua ? Array.prototype.slice.call(this.regua.querySelectorAll('[data-apl-tom]')) : [];
      this.itens = Array.prototype.slice.call(this.querySelectorAll('[data-apl-item]'));
      if (!this.regua || !this.celulas.length || !this.itens.length) return;

      this._abort = new AbortController();
      var sig = this._abort.signal;
      this.regua.addEventListener('click', this.aoClicarRegua.bind(this), { signal: sig });
      this.regua.addEventListener('keydown', this.aoTeclarRegua.bind(this), { signal: sig });
      // Com a oferta de tons (Base, BBS) a aplicação segue a 1a unidade dela, em silêncio.
      var seguir = this.aoMudarOferta.bind(this);
      document.addEventListener('pdp-offer:variant-changed', seguir, { signal: sig });
      document.addEventListener('pdp-offer:tier-changed', seguir, { signal: sig });
      this._pronto = true;
      this.alcancarOferta();
    }

    disconnectedCallback() {
      if (this._timerMedida) { clearTimeout(this._timerMedida); this._timerMedida = null; }
      if (this._abort) this._abort.abort();
      this._pronto = false;
    }

    reduzido() {
      return midia('(prefers-reduced-motion: reduce)');
    }

    // Só pede play com o palco na tela (spec 4.3). Quem clicou ou teclou na régua
    // está olhando para ela, mas a régua pode ficar no rodapé da tela com o vídeo abaixo.
    naTela(el) {
      if (!el.getBoundingClientRect) return true;
      var r = el.getBoundingClientRect();
      var h = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
      return r.bottom > 0 && (!h || r.top < h);
    }

    // Mesma escolha do aplicarPoster do biobio-trio.js; idempotente.
    poster(video) {
      if (video.hasAttribute('poster')) return;
      var url = (midia(LAYOUT_POSTER_CELULAR) && video.getAttribute('data-poster-mobile')) || video.getAttribute('data-poster');
      if (url) video.setAttribute('poster', url);
    }

    tocar(video) {
      this.poster(video);
      if (this.reduzido()) return;
      if (!video.src && video.dataset.src) video.src = video.dataset.src;
      // Escolher o tom é pedir para ver: uma pausa antiga da cliente neste vídeo deixa de valer.
      delete video.dataset.userPaused;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    // Só a seleção: célula ativa, figura visível, vídeo que sai pausado. Não toca vídeo
    // nem mede. Devolve true se trocou de figura.
    selecionar(variantId) {
      var id = String(variantId);
      var atual = null, novo = null;
      this.itens.forEach(function (f) {
        if (!f.hidden && !atual) atual = f;
        if (f.dataset.aplItem === id) novo = f;
      });
      if (!novo || novo === atual) return false;

      if (atual) {
        var va = atual.querySelector('video');
        // Sem userPaused: o observer do Trio volta a tocar quando este tom reaparecer.
        if (va) va.pause();
        atual.hidden = true;
      }
      novo.hidden = false;
      this.marcarRegua(id);
      this.dispatchEvent(new CustomEvent('aplicacao:tom-trocado', { detail: { variantId: Number(id) }, bubbles: true }));
      return true;
    }

    // Uso manual da régua: selecionar + tocar (se o palco está na tela).
    aplicarTom(variantId) {
      if (!this.selecionar(variantId)) return false;
      var novo = null, id = String(variantId);
      this.itens.forEach(function (f) { if (f.dataset.aplItem === id) novo = f; });
      var vn = novo && novo.querySelector('video');
      if (vn) {
        if (this.naTela(novo)) this.tocar(vn);
        else this.poster(vn);
      }
      return true;
    }

    // Acompanha a oferta: troca a figura SEM tocar (não religa vídeo pausado pela
    // cliente) e SEM medir (pdp_v2_aplicacao_tom é só de uso manual). Cor sem vídeo
    // (id sem célula) cai na primeira célula, nunca fica o vídeo da cor anterior.
    sincronizar(variantId) {
      var id = String(variantId);
      if (!this.celulaDe(id)) id = this.celulas[0].dataset.aplTom;
      if (!this.selecionar(id)) return false;
      var novo = null;
      this.itens.forEach(function (f) { if (f.dataset.aplItem === id) novo = f; });
      var vn = novo && novo.querySelector('video');
      if (vn) this.poster(vn);
      return true;
    }

    // detail.selected da oferta: { direct, qty } ou { tier, units: [id|null, ...] }.
    // Mesma normalização do antes e depois: 1a unidade, ou a seleção direta. Payload
    // ruim, evento antes do setup ou página sem oferta com tons nunca lança.
    aoMudarOferta(e) {
      var sel = e && e.detail && e.detail.selected;
      if (!sel || typeof sel !== 'object' || !this._pronto || !this.celulas || !this.celulas.length) return;
      if (!document.querySelector('pdp-offer.pdp-offer--tons')) return;
      var id = (Array.isArray(sel.units) && sel.units[0]) || sel.direct;
      if (id === null || id === undefined || id === '') return;
      this.sincronizar(id);
    }

    alcancarOferta() {
      try {
        var oferta = document.querySelector('pdp-offer.pdp-offer--tons');
        if (!oferta || typeof oferta.snapshotSelected !== 'function') return;
        this.aoMudarOferta({ detail: { selected: oferta.snapshotSelected() } });
      } catch (e) { /* oferta ainda sem setup: os eventos alcançam */ }
    }

    nomeDaCelula(cel) {
      if (!cel) return '';
      return (cel.dataset && cel.dataset.aplNome) || nomeDoTom(cel.getAttribute('aria-label'));
    }

    medir(id) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'pdp_v2_aplicacao_tom', tom: this.nomeDaCelula(this.celulaDe(id)) });
    }

    // Teclado: só o tom em que a cliente parou conta (as setas atravessam os tons no caminho).
    medirDepois(id) {
      var self = this;
      if (this._timerMedida) clearTimeout(this._timerMedida);
      this._timerMedida = setTimeout(function () { self._timerMedida = null; self.medir(id); }, ESPERA_TECLADO);
    }

    celulaDe(id) {
      for (var i = 0; i < this.celulas.length; i++) {
        if (this.celulas[i].dataset.aplTom === id) return this.celulas[i];
      }
      return null;
    }

    // Um só tabindex=0 no grupo, que é o padrão de radiogroup.
    marcarRegua(id) {
      this.celulas.forEach(function (c) {
        var ativo = c.dataset.aplTom === String(id);
        c.classList.toggle('is-ativo', ativo);
        c.setAttribute('aria-checked', ativo ? 'true' : 'false');
        c.tabIndex = ativo ? 0 : -1;
      });
    }

    aoClicarRegua(e) {
      var cel = e.target && e.target.closest ? e.target.closest('[data-apl-tom]') : null;
      if (!cel) return;
      if (this.aplicarTom(cel.dataset.aplTom)) this.medir(cel.dataset.aplTom);
    }

    // Setinha anda entre os tons (com volta), Home/End vão aos extremos; mover o foco já seleciona.
    aoTeclarRegua(e) {
      // Alt/Ctrl/Cmd + seta é atalho do navegador (voltar, trocar de aba): passa reto.
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      var i = this.celulas.indexOf(document.activeElement);
      if (i < 0) return;
      var novo = PdpAplicacao.proximoIndice(i, this.celulas.length, e.key);
      if (novo < 0) return;
      e.preventDefault();
      var id = this.celulas[novo].dataset.aplTom;
      if (this.aplicarTom(id)) this.medirDepois(id);
      this.celulas[novo].focus();
    }

    static proximoIndice(i, n, k) {
      if (k === 'ArrowRight' || k === 'ArrowDown') return (i + 1) % n;
      if (k === 'ArrowLeft' || k === 'ArrowUp') return (i - 1 + n) % n;
      if (k === 'Home') return 0;
      if (k === 'End') return n - 1;
      return -1;
    }
  }

  PdpAplicacao.nomeDoTom = nomeDoTom;
  window.customElements.define('pdp-aplicacao', PdpAplicacao);
})();
